import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, FileText, Download, LogOut, ArrowLeft, WifiOff, LayoutGrid } from "lucide-react";
import { api, bg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Nucleo, PainelIA, MinhasFichas, ASSISTENTE, fichasGuardadas } from "@/components/InnovaIAAluna";
import { FichaTecnica } from "@/components/IATexto";
import { InstallDialog, useInstall } from "@/components/InstallApp";

/* App da Keyla Sampaio Virtual (/keyla): só a conversa com a IEP Intelligence, em tela cheia, que instala no celular
   com ícone próprio. Entra com a mesma conta da plataforma e segue as mesmas regras (limite de aparelhos, inatividade).
   Sem internet abre as fichas técnicas salvas neste aparelho. */
const FUNDO = "linear-gradient(135deg,#1A2340,#232D4F 60%,#1E9E2A)";
const APP = { nome: "Pergunte à Keyla", icone: "/brand/keyla/icon-192.png", desc: "Tire dúvidas do Método com um toque, na hora do atendimento.", de: "do Pergunte à Keyla", abre: "o Pergunte à Keyla abre" };

function Topo({ children }) {
  return (
    <div className="flex items-center gap-2 px-4 pb-3 text-white" style={{ background: FUNDO, paddingTop: "max(12px, env(safe-area-inset-top))" }}>
      <Nucleo tamanho={36} />
      <div className="min-w-0">
        <div className="font-display font-bold leading-tight tracking-wide">IEP <span className="text-[#A7F3B0]">INTELLIGENCE</span></div>
        <div className="truncate text-[11px] text-slate-200">{ASSISTENTE}</div>
      </div>
      <div className="ml-auto flex items-center gap-1">{children}</div>
    </div>
  );
}
const BotaoTopo = ({ children, ...p }) => <button {...p} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1.5 text-[12px] font-semibold hover:bg-white/20">{children}</button>;

/** Sem internet: só as fichas guardadas neste aparelho (as perguntas precisam do servidor). */
function SemInternet() {
  const [aberta, setAberta] = useState(null);
  const lista = fichasGuardadas() || [];
  return (
    <div className="flex h-[100dvh] flex-col bg-[#F8FAFC]">
      <Topo />
      <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">
        <WifiOff className="mt-0.5 h-4 w-4 shrink-0" /> Sem internet agora. Suas fichas salvas abrem normalmente; para perguntar à Keyla, conecte-se de novo.
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3">
        {aberta ? (
          <>
            <button onClick={() => setAberta(null)} className="mb-2 inline-flex items-center gap-1 text-[13px] font-semibold text-slate-600"><ArrowLeft className="h-4 w-4" /> Minhas fichas</button>
            <FichaTecnica bloco={aberta.texto} revisada={aberta.revisada} />
          </>
        ) : lista.length === 0 ? <p className="px-2 py-8 text-center text-sm text-slate-500">Nenhuma ficha guardada neste aparelho ainda. Com internet, abra “Fichas” uma vez e elas ficam disponíveis aqui.</p>
          : <div className="space-y-2">{lista.map((f) => (
              <button key={f.id} onClick={() => setAberta(f)} className="flex w-full items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left">
                <FileText className="mt-0.5 h-4 w-4 shrink-0 text-[#1E9E2A]" />
                <span className="min-w-0 flex-1 truncate font-semibold text-slate-800">{f.titulo}</span>
              </button>))}</div>}
      </div>
      <button onClick={() => window.location.reload()} className="m-3 rounded-xl py-3 text-[15px] font-bold text-white" style={{ background: "#1E9E2A" }}>Tentar de novo</button>
    </div>
  );
}

export default function KeylaApp() {
  const { user, logout } = useAuth();
  const [st, setSt] = useState(null);            // null = carregando, false = IA não liberada nesta conta
  const [fichas, setFichas] = useState(false);
  const [instalar, setInstalar] = useState(false);
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  const { installed } = useInstall();
  const carregar = useCallback(() => api.get("/innova-ia/status", bg).then((r) => setSt(r.data)).catch((e) => { if (!e.response) setOffline(true); else setSt(false); }), []);
  useEffect(() => { if (user) carregar(); }, [user, carregar]);
  // a conta não carregou: sem internet mostra as fichas do aparelho; com internet vai para o login
  const [conferiu, setConferiu] = useState(false);
  useEffect(() => {
    if (user !== false || conferiu) return;
    api.get("/auth/me", bg).catch((e) => { if (!e.response) setOffline(true); }).finally(() => setConferiu(true));
  }, [user, conferiu]);
  useEffect(() => {
    const on = () => setOffline(false), off = () => setOffline(true);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  if (offline && !st) return <SemInternet />;
  if (user === null) return <div className="grid h-[100dvh] place-items-center" style={{ background: FUNDO }}><Nucleo tamanho={64} pensando /></div>;
  if (!user && !conferiu) return <div className="grid h-[100dvh] place-items-center" style={{ background: FUNDO }}><Nucleo tamanho={64} pensando /></div>;
  if (!user) return <Navigate to="/login?next=keyla" replace />;
  if (user.onboarding_required) return <Navigate to="/primeiro-acesso" replace />;
  const sair = async () => { await logout(); window.location.assign("/login?next=keyla"); };

  return (
    <div className="flex h-[100dvh] flex-col bg-[#F8FAFC]" data-testid="keyla-app">
      <Topo>
        {st && <BotaoTopo onClick={() => setFichas(!fichas)} data-testid="keyla-fichas"><FileText className="h-3.5 w-3.5" /> {fichas ? "Conversa" : "Fichas"}</BotaoTopo>}
        <BotaoTopo onClick={sair} aria-label="Sair" title="Sair"><LogOut className="h-3.5 w-3.5" /></BotaoTopo>
      </Topo>
      {!installed && (
        <button onClick={() => setInstalar(true)} className="flex items-center gap-2 border-b border-green-200 bg-green-50 px-4 py-2 text-left text-[13px] text-green-800" data-testid="keyla-instalar">
          <Download className="h-4 w-4 shrink-0" /> <span><b>Instale o app Pergunte à Keyla no celular</b> e abra com um toque, na hora do atendimento.</span>
        </button>
      )}
      {st === null ? <div className="grid flex-1 place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        : st === false ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-8 text-center">
            <span className="grid h-16 w-16 place-items-center rounded-full bg-[#1A2340]"><Nucleo tamanho={50} /></span>
            <p className="font-semibold text-slate-700">Olá, {String(user.name || "").split(" ")[0]}! A {ASSISTENTE} ainda não está liberada na sua conta.</p>
            <p className="text-sm text-slate-500">Assim que for liberada, é só abrir este app e perguntar.</p>
            <a href="/app" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A]"><LayoutGrid className="h-4 w-4" /> Ir para a plataforma</a>
          </div>
        )
        : fichas ? <MinhasFichas voltar={() => setFichas(false)} />
        : <PainelIA st={st} setSt={setSt} carregar={carregar} />}
      <InstallDialog open={instalar} onOpenChange={setInstalar} app={APP} />
    </div>
  );
}
